import { Slide } from '../types/slide'

export const slides: Slide[] = [
  // Cover Slide
  {
    type: 'title',
    title: 'God\'s Sovereign Control Over All',
    subtitle: 'A Study of Daniel Chapter 7',
    content: 'The Vision of the Four Beasts and the Eternal Kingdom',
    image: '/images/the throne of God.png',
    imageAlt: 'The throne of God - representing His sovereign control over all creation and history',
    points: [
      'Understanding God\'s Plan Through History',
      'The Rise and Fall of World Empires',
      'Divine Sovereignty Over Human Kingdoms',
      'The Promise of the Eternal Kingdom',
      'Bible Version: English Standard Version (ESV)'
    ]
  },

  // Opening Title Slide
  {
    type: 'title',
    title: 'The Book of Daniel',
    subtitle: 'Chapter 7 Study',
    content: 'The Vision of the Four Beasts',
    image: '/images/the-four-beasts.png', // Replace with your main four beasts image
    imageAlt: 'The four beasts from Daniel Chapter 7 - Lion with eagle wings, Bear, Leopard with four wings, and Terrifying beast with iron teeth',
    points: [
      'A Prophetic Vision of World Empires',
      'The Rise and Fall of Earthly Kingdoms',
      'God\'s Sovereign Control Over History',
      'The Coming of the Eternal Kingdom',
      'Bible Version: English Standard Version (ESV)'
    ]
  },

  // Introduction Slide
  {
    type: 'title',
    title: 'Daniel Chapter 7',
    subtitle: 'The Vision of the Four Beasts',
    content: 'A Prophetic Vision of World Empires and God\'s Eternal Kingdom',
    verse: 'In the first year of Belshazzar king of Babylon, Daniel had a dream, and visions passed through his mind as he was lying in bed. He wrote down the substance of his dream.',
    verseReference: 'Daniel 7:1 (ESV)'
  },

  // Introduction
  {
    type: 'explanation',
    title: 'The Historical Context',
    content: 'This vision occurred during the reign of Belshazzar, the last king of Babylon, around 553 BC. Daniel was approximately 70 years old when he received this vision.',
    points: [
      'Daniel was in exile in Babylon for over 50 years',
      'The vision came during a time of political uncertainty',
      'This chapter marks a transition from historical narrative to apocalyptic prophecy',
      'The vision reveals God\'s sovereign control over world history'
    ]
  },

  // The Vision Begins
  {
    type: 'verse',
    title: 'The Vision Begins',
    verse: 'Daniel said: "In my vision at night I looked, and there before me were the four winds of heaven churning up the great sea. Four great beasts, each different from the others, came up out of the sea."',
    verseReference: 'Daniel 7:2-3 (ESV)',
    content: 'The "great sea" represents the nations and peoples of the world, and the "four winds" symbolize the forces of heaven stirring up these nations.'
  },

  // First Beast - Lion with Eagle's Wings
  {
    type: 'beast',
    title: 'The First Beast',
    verse: 'The first was like a lion, and it had the wings of an eagle. I watched until its wings were torn off and it was lifted from the ground so that it stood on two feet like a human being, and the mind of a human was given to it.',
    verseReference: 'Daniel 7:4 (ESV)',
    image: '/images/lion-with-eaglewings.png', // Lion with eagle wings image
    imageAlt: 'Lion with eagle wings representing the Babylonian Empire',
    beasts: [
      {
        name: 'Lion with Eagle\'s Wings',
        description: 'Represents the Babylonian Empire (605-539 BC). The lion symbolizes strength and majesty, while the eagle\'s wings represent speed and dominance. The removal of wings and giving of a human mind symbolizes Nebuchadnezzar\'s humbling and restoration.',
        verse: 'Daniel 7:4',
        image: '/images/lion-with-eaglewings.png' // Lion with eagle wings image
      }
    ]
  },

  // Second Beast - Bear
  {
    type: 'beast',
    title: 'The Second Beast',
    verse: 'And there before me was a second beast, which looked like a bear. It was raised up on one of its sides, and it had three ribs in its mouth between its teeth. It was told, "Get up and eat your fill of flesh!"',
    verseReference: 'Daniel 7:5 (ESV)', // Replace with your bear with three ribs image
    imageAlt: 'Bear raised on one side representing the Medo-Persian Empire',
    beasts: [
      {
        name: 'Bear Raised on One Side',
        description: 'Represents the Medo-Persian Empire (539-331 BC). The bear symbolizes strength and ferocity. The three ribs represent the three major conquests: Lydia, Babylon, and Egypt. Being raised on one side indicates the Persian dominance over the Medes.',
        verse: 'Daniel 7:5',
        image: '/images/bear-creature.png' // Replace with your bear with three ribs image
      }
    ]
  },

  // Third Beast - Leopard with Four Wings
  {
    type: 'beast',
    title: 'The Third Beast',
    verse: 'After that, I looked, and there before me was another beast, one that looked like a leopard. And on its back it had four wings like those of a bird. This beast had four heads, and it was given authority to rule.',
    verseReference: 'Daniel 7:6 (ESV)',
    image: '/images/leopard-with-4-wings.png', // Replace with your leopard with four wings image
    imageAlt: 'Leopard with four wings and four heads representing the Greek Empire',
    beasts: [
      {
        name: 'Leopard with Four Wings and Four Heads',
        description: 'Represents the Greek Empire under Alexander the Great (331-168 BC). The leopard symbolizes speed and agility. The four wings represent the rapid conquests, and the four heads represent the four generals who divided Alexander\'s empire after his death.',
        verse: 'Daniel 7:6',
        image: '/images/leopard-with-4-wings.png' // Replace with your leopard with four wings image
      }
    ]
  },

  // Fourth Beast - Terrifying and Dreadful
  {
    type: 'beast',
    title: 'The Fourth Beast',
    verse: 'After that, in my vision at night I looked, and there before me was a fourth beast—terrifying and frightening and very powerful. It had large iron teeth; it crushed and devoured its victims and trampled underfoot whatever was left. It was different from all the former beasts, and it had ten horns.',
    verseReference: 'Daniel 7:7 (ESV)',
    image: '/images/beast-with-horns.png', // Replace with your terrifying beast image
    imageAlt: 'Terrifying beast with iron teeth and ten horns representing the Roman Empire',
    beasts: [
      {
        name: 'Terrifying Beast with Iron Teeth and Ten Horns',
        description: 'Represents the Roman Empire (168 BC - 476 AD). This beast is more terrifying than the others, symbolizing Rome\'s unprecedented power and cruelty. The iron teeth represent Rome\'s military might, and the ten horns represent the ten kingdoms that emerged from the Roman Empire.',
        verse: 'Daniel 7:7',
        image: '/images/fourth beast-1.png' // Replace with your terrifying beast image
      }
    ]
  },

  // The Little Horn
  {
    type: 'explanation',
    title: 'The Little Horn',
    verse: 'While I was thinking about the horns, there before me was another horn, a little one, which came up among them; and three of the first horns were uprooted before it. This horn had eyes like the eyes of a human being and a mouth that spoke boastfully.',
    verseReference: 'Daniel 7:8',
    content: 'The little horn represents a future antichrist figure who will arise from the ten kingdoms. This figure will be characterized by intelligence, arrogance, and blasphemy against God.',
    points: [
      'The little horn uproots three of the ten horns',
      'It has human-like eyes, symbolizing intelligence and cunning',
      'It speaks boastfully, indicating pride and blasphemy',
      'This represents the final world ruler before Christ\'s return'
    ]
  },

  // The Ancient of Days
  {
    type: 'verse',
    title: 'The Ancient of Days',
    verse: 'As I looked, thrones were set in place, and the Ancient of Days took his seat. His clothing was as white as snow; the hair of his head was white like wool. His throne was flaming with fire, and its wheels were all ablaze.',
    image: '/images/ancient-of-days.png', // Replace with your terrifying beast image
    imageAlt: 'Ancient of Days',
    verseReference: 'Daniel 7:9 (ESV)',
    content: 'This is a vision of God the Father in His heavenly court, preparing to judge the nations and establish His eternal kingdom.'
  },

  // The Court in Session
  {
    type: 'verse',
    title: 'The Court in Session',
    verse: 'A river of fire was flowing, coming out from before him. Thousands upon thousands attended him; ten thousand times ten thousand stood before him. The court was seated, and the books were opened.',
    verseReference: 'Daniel 7:10',
    image: '/images/heaven-open-books.png', // Replace with your terrifying beast image
    imageAlt: 'Havenly Court in Session',
    content: 'The heavenly court is now in session, with countless angels in attendance. The books contain the records of all human deeds and the divine plan for history.'
  },

  // The Beast Destroyed
  {
    type: 'verse',
    title: 'The Beast Destroyed',
    verse: 'Then I continued to watch because of the boastful words the horn was speaking. I kept looking until the beast was slain and its body destroyed and thrown into the blazing fire. (The other beasts had been stripped of their authority, but were allowed to live for a period of time.)',
    verseReference: 'Daniel 7:11-12',
    content: 'The fourth beast and the little horn are completely destroyed, while the first three beasts are allowed to continue for a time, representing the ongoing influence of their respective cultures.'
  },

  // The Son of Man
  {
    type: 'verse',
    title: 'The Son of Man',
    verse: 'In my vision at night I looked, and there before me was one like a son of man, coming with the clouds of heaven. He approached the Ancient of Days and was led into his presence. He was given authority, glory and sovereign power; all nations and peoples of every language worshiped him. His dominion is an everlasting dominion that will not pass away, and his kingdom is one that will never be destroyed.',
    verseReference: 'Daniel 7:13-14 (ESV)',
    content: 'This is the most important part of the vision - the coming of the Messiah, Jesus Christ, who will establish God\'s eternal kingdom on earth.'
  },

  // The Interpretation Begins
  {
    type: 'explanation',
    title: 'Daniel Seeks Understanding',
    verse: 'I, Daniel, was troubled in spirit, and the visions that passed through my mind disturbed me. I approached one of those standing there and asked him the true meaning of all this. So he told me and gave me the interpretation of these things.',
    verseReference: 'Daniel 7:15-16',
    content: 'Daniel is deeply troubled by what he has seen and seeks divine interpretation. An angel is sent to explain the meaning of the vision.',
    points: [
      'Daniel was deeply disturbed by the vision',
      'He sought divine interpretation from an angel',
      'The angel provided the meaning of the symbols',
      'This shows the importance of divine revelation for understanding prophecy'
    ]
  },

  // The Four Kingdoms Explained
  {
    type: 'explanation',
    title: 'The Four Kingdoms Explained',
    verse: 'The four great beasts are four kings that will arise from the earth. But the holy people of the Most High will receive the kingdom and will possess it forever—yes, for ever and ever.',
    verseReference: 'Daniel 7:17-18',
    content: 'The angel confirms that the four beasts represent four earthly kingdoms, but emphasizes that God\'s people will ultimately inherit the eternal kingdom.',
    points: [
      'The four beasts represent four earthly kingdoms',
      'These kingdoms will rise and fall in succession',
      'God\'s people will ultimately inherit the eternal kingdom',
      'The eternal kingdom will never be destroyed'
    ]
  },

  // The Prophetic Timeline Question
  {
    type: 'explanation',
    title: 'A Critical Prophetic Question',
    content: 'Why is Daniel shown a vision of four world powers starting with Babylon, then moving on to Medo-Persia, Greece and Rome, and what do those kingdoms have to do with the last days and the Antichrist?',
    points: [
      'How do you get to the end of Rome and jump ahead to the end of all things?',
      'The end of all things is still ahead of you and me',
      'But the end of Rome happened around the fifth century A.D.',
      'What\'s the connection between these four kingdoms and the Antichrist?',
      'Doesn\'t the Antichrist come and reveal himself at the beginning of the great tribulation?',
      'That\'s still well off in the future',
      'This is actually a very good question that requires careful study'
    ]
  },

  // The Fourth Beast Detailed
  {
    type: 'explanation',
    title: 'The Fourth Beast Detailed',
    verse: 'Then I wanted to know the meaning of the fourth beast, which was different from all the others and most terrifying, with its iron teeth and bronze claws—the beast that crushed and devoured its victims and trampled underfoot whatever was left.',
    verseReference: 'Daniel 7:19',
    content: 'Daniel specifically asks about the fourth beast because it is the most terrifying and different from all the others.',
    points: [
      'The fourth beast is the most terrifying of all',
      'It has iron teeth and bronze claws',
      'It crushes and devours its victims',
      'It tramples underfoot whatever is left'
    ]
  },

  // The Ten Horns and Little Horn
  {
    type: 'explanation',
    title: 'The Ten Horns and Little Horn',
    verse: 'I also wanted to know about the ten horns on its head and about the other horn that came up, before which three of them fell—the horn that looked more imposing than the others and that had eyes and a mouth that spoke boastfully.',
    verseReference: 'Daniel 7:20',
    content: 'Daniel seeks clarification about the ten horns and the little horn that emerges among them.',
    points: [
      'The ten horns represent ten kings from the fourth kingdom',
      'The little horn is more imposing than the others',
      'It has eyes and a mouth that speaks boastfully',
      'Three of the ten horns fall before it'
    ]
  },

  // The Little Horn's Activities
  {
    type: 'explanation',
    title: 'The Little Horn\'s Activities',
    verse: 'As I watched, this horn was waging war against the holy people and defeating them, until the Ancient of Days came and pronounced judgment in favor of the holy people of the Most High, and the time came when they possessed the kingdom.',
    verseReference: 'Daniel 7:21-22',
    content: 'The little horn will wage war against God\'s people, but God will ultimately judge in favor of His people and give them the kingdom.',
    points: [
      'The little horn wages war against God\'s people',
      'It appears to defeat them for a time',
      'God will ultimately judge in favor of His people',
      'The time will come when God\'s people possess the kingdom'
    ]
  },

  // The Final Interpretation
  {
    type: 'verse',
    title: 'The Final Interpretation',
    verse: 'He gave me this explanation: "The fourth beast is a fourth kingdom that will appear on earth. It will be different from all the other kingdoms and will devour the whole earth, trampling it down and crushing it. The ten horns are ten kings who will come from this kingdom. After them another king will arise, different from the earlier ones; he will subdue three kings. He will speak against the Most High and oppress his holy people and try to change the set times and the laws. The holy people will be delivered into his hands for a time, times and half a time."',
    verseReference: 'Daniel 7:23-25',
    content: 'The angel provides the complete interpretation of the vision, explaining the fourth kingdom, the ten kings, and the final antichrist figure.'
  },

  // The Final Judgment
  {
    type: 'verse',
    title: 'The Final Judgment',
    verse: 'But the court will sit, and his power will be taken away and completely destroyed forever. Then the sovereignty, power and greatness of all the kingdoms under heaven will be handed over to the holy people of the Most High. His kingdom will be an everlasting kingdom, and all rulers will worship and obey him.',
    verseReference: 'Daniel 7:26-27',
    content: 'The antichrist will be judged and destroyed, and all authority will be given to God\'s people in His eternal kingdom.'
  },

  // Daniel's Response
  {
    type: 'verse',
    title: 'Daniel\'s Response',
    verse: 'This is the end of the matter. I, Daniel, was deeply troubled by my thoughts, and my face turned pale, but I kept the matter to myself.',
    verseReference: 'Daniel 7:28',
    content: 'Daniel is deeply affected by the vision and its interpretation, but keeps the matter to himself as instructed.'
  },

  // Summary Table
  {
    type: 'table',
    title: 'Daniel 2 vs Daniel 7 Comparison',
    content: 'A comparison of the two major prophetic visions in Daniel showing the same four world empires from different perspectives.',
    table: [
      {
        daniel2: 'Head of Gold',
        daniel7: 'Lion with Eagle\'s Wings',
        empire: 'Babylonian Empire',
        timeframe: '605-539 BC'
      },
      {
        daniel2: 'Chest & Arms of Silver',
        daniel7: 'Bear Raised on One Side',
        empire: 'Medo-Persian Empire',
        timeframe: '539-331 BC'
      },
      {
        daniel2: 'Belly & Thighs of Bronze',
        daniel7: 'Leopard with Four Wings & Four Heads',
        empire: 'Greek Empire',
        timeframe: '331-168 BC'
      },
      {
        daniel2: 'Legs of Iron',
        daniel7: 'Terrifying Beast with Iron Teeth & Ten Horns',
        empire: 'Roman Empire',
        timeframe: '168 BC - 476 AD'
      },
      {
        daniel2: 'Feet of Iron & Clay',
        daniel7: 'Little Horn (Antichrist)',
        empire: 'Revived Roman Empire',
        timeframe: 'Future'
      },
      {
        daniel2: 'Stone Crushes All',
        daniel7: 'Son of Man Receives Kingdom',
        empire: 'Eternal Kingdom of God',
        timeframe: 'Eternal'
      }
    ]
  },

  // Key Themes
  {
    type: 'explanation',
    title: 'Key Themes of Daniel 7',
    content: 'This chapter reveals several important themes that are central to biblical prophecy and eschatology.',
    points: [
      'God\'s sovereign control over world history',
      'The rise and fall of earthly kingdoms',
      'The ultimate triumph of God\'s kingdom',
      'The coming of the Messiah and His eternal reign',
      'The final judgment of the wicked',
      'The eternal inheritance of God\'s people'
    ]
  },

  // Prophetic Fulfillment
  {
    type: 'explanation',
    title: 'Prophetic Fulfillment',
    content: 'The first three beasts have been historically fulfilled, while the fourth beast and little horn point to future events.',
    points: [
      'Babylonian Empire (605-539 BC) - Fulfilled',
      'Medo-Persian Empire (539-331 BC) - Fulfilled',
      'Greek Empire (331-168 BC) - Fulfilled',
      'Roman Empire (168 BC - 476 AD) - Fulfilled',
      'Ten Kingdoms - Future fulfillment',
      'Little Horn (Antichrist) - Future fulfillment',
      'Eternal Kingdom - Future fulfillment'
    ]
  },

  // Application for Today
  {
    type: 'explanation',
    title: 'Application for Today',
    content: 'This vision has profound implications for how we live as Christians in the present age.',
    points: [
      'We can trust in God\'s sovereign control over history',
      'We should not be afraid of earthly powers and kingdoms',
      'We must remain faithful during times of persecution',
      'We have hope in the coming eternal kingdom',
      'We should live with an eternal perspective',
      'We must be ready for Christ\'s return'
    ]
  },

  // Conclusion
  {
    type: 'conclusion',
    title: 'The Eternal Kingdom',
    content: 'Daniel\'s vision ultimately points to the triumph of God\'s eternal kingdom over all earthly powers.',
    verse: 'Then the sovereignty, power and greatness of all the kingdoms under heaven will be handed over to the holy people of the Most High. His kingdom will be an everlasting kingdom, and all rulers will worship and obey him.',
    verseReference: 'Daniel 7:27 (ESV)',
    points: [
      'God\'s kingdom will ultimately triumph',
      'All earthly powers will be judged',
      'God\'s people will inherit the eternal kingdom',
      'Christ will reign forever and ever',
      'This gives us hope and confidence in the present age'
    ]
  },

  // Interpretation Questions
  {
    type: 'explanation',
    title: 'Interpretation Questions',
    content: 'Let\'s explore the deeper meaning and significance of Daniel\'s vision through these important questions.',
    points: [
      'Why does the narrative go back to Belshazzar?',
      'Who are the four great beasts?',
      'Is there any significance to how the beasts are described?',
      'Why does the fourth beast seem to be given more significance?',
      'What do the horns of the fourth beast signify?',
      'Who is the Ancient of Days?',
      'How is the Ancient of Days described, and do we see any similarity to how he is mentioned to someone in the New Testament?',
      'What happens to the beasts?',
      'Who is the Son of Man?',
      'Who are the saints of the Most High?',
      'Who finally gets the kingdom? And what is this kingdom mentioned?'
    ]
  },

  // Application Questions
  {
    type: 'explanation',
    title: 'Application Questions',
    content: 'Now let\'s consider how this ancient prophecy applies to our lives today.',
    points: [
      'Who do you see the Kingdoms belonging to today?',
      'Do you think God is in control?',
      'If we do not see the kingdom belonging to God right now, what can we do in the meantime?'
    ]
  },

  // Discussion and Reflection
  {
    type: 'explanation',
    title: 'Discussion and Reflection',
    content: 'Take time to discuss these questions with your study group and reflect on the implications for your faith.',
    points: [
      'Share your thoughts on the interpretation questions',
      'Discuss how the prophecy applies to current world events',
      'Consider what it means to live as citizens of God\'s eternal kingdom',
      'Reflect on God\'s sovereignty in your personal life',
      'Pray for wisdom and understanding of God\'s plan'
    ]
  },

  // Final Encouragement
  {
    type: 'conclusion',
    title: 'Final Encouragement',
    content: 'May this study of Daniel Chapter 7 strengthen your faith and give you hope in God\'s ultimate victory.',
    verse: 'But the court will sit, and his power will be taken away and completely destroyed forever. Then the sovereignty, power and greatness of all the kingdoms under heaven will be handed over to the holy people of the Most High. His kingdom will be an everlasting kingdom, and all rulers will worship and obey him.',
    verseReference: 'Daniel 7:26-27',
    points: [
      'God is in control of all world events',
      'His kingdom will ultimately triumph',
      'We are citizens of an eternal kingdom',
      'Live with hope and confidence in God\'s promises',
      'Share this hope with others'
    ]
  }
]
